import { useEffect, useRef, useState } from 'react';
import { roomWinTotals } from '@big2/shared';
import { useGameState } from '../net/GameProvider.js';
import { useI18n } from '../i18n.js';
import { getUserAvatar } from '../utils/avatar.js';
import { Icon } from './Icon.js';
import { PlayerProfileModal } from './profile/PlayerProfileModal.js';
import './history.css';

export function RoomHistory({ onClose }: { onClose: () => void }) {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';

  const [selectedId, setSelectedId] = useState(state.roomHistoryLinks?.[0]?.id ?? '');
  const [tab, setTab] = useState<'standings' | 'games'>('standings');
  const [copied, setCopied] = useState(false);
  const [inspectedUserId, setInspectedUserId] = useState<{ userId: string; name: string } | null>(null);

  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const linksRef = useRef(state.roomHistoryLinks ?? []);
  linksRef.current = state.roomHistoryLinks ?? [];

  useEffect(() => {
    const link = (linksRef.current ?? []).find((item) => item.id === selectedId);
    if (link && state.connectionStatus === 'connected') {
      actionsRef.current.loadRoomHistory(link);
    }
  }, [selectedId, state.connectionStatus, state.lastGameOver]);

  const history = state.roomHistory?.id === selectedId ? state.roomHistory : null;
  const totals = history ? roomWinTotals(history) : [];
  const champion = totals[0];

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(isMn ? 'mn-MN' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const formatRelative = (iso: string) => {
    try {
      const diffMs = Date.now() - Date.parse(iso);
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return isMn ? 'Дөнгөж сая' : 'Just now';
      if (mins < 60) return isMn ? `${mins}м өмнө` : `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return isMn ? `${hours}ц өмнө` : `${hours}h ago`;
      return formatDate(iso);
    } catch {
      return '';
    }
  };

  const formatGameTypeName = (gameType: string) => {
    switch (gameType) {
      case 'muushig':
        return isMn ? '♣ Муушиг' : '♣ Muushig';
      case 'twentyone':
        return isMn ? '♦ 21' : '♦ 21';
      case 'daaluu':
        return isMn ? '🂍 Даалуу' : '🂍 Daaluu';
      default:
        return isMn ? '♠ Арван гурав' : '♠ Big Two';
    }
  };

  const copyStandings = async () => {
    if (!history) return;
    const medals = ['🥇', '🥈', '🥉'];
    const lines = [
      `🏆 Big Two & Muushig — ${isMn ? 'Өрөөний дүн' : 'Room Standings'} (${history.roomId})`,
      `${isMn ? 'Нийт тоглолт' : 'Total games'}: ${history.games.length}`,
      '',
      ...totals.map((p, i) => {
        const prefix = medals[i] ? `${medals[i]} ${i + 1}.` : `${i + 1}.`;
        const wins = p.big2 + p.muushig + p.twentyone + p.daaluu;
        return `${prefix} ${p.name}: ${wins} ${isMn ? 'хожил' : 'wins'} (${p.played} ${isMn ? 'тоглосон' : 'played'})`;
      }),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard write failed */
    }
  };

  return (
    <section
      className="history-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="history-title"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="history-modal-container">
        {/* Header */}
        <header className="history-heading">
          <div className="history-title-group">
            <span className="history-icon" aria-hidden="true">🏆</span>
            <h2 id="history-title">{isMn ? 'Өрөөний тоглолтын дүн' : 'Room Match Summary'}</h2>
          </div>
          <button
            className="history-close-btn"
            onClick={onClose}
            title={isMn ? 'Хаах' : 'Close history'}
            aria-label={isMn ? 'Хаах' : 'Close history'}
          >
            <Icon name="x" size={18} />
          </button>
        </header>

        {/* Room Sessions Selector */}
        {state.roomHistoryLinks.length > 0 && (
          <div className="history-sessions-selector">
            <div className="history-sessions-label">
              <span>{isMn ? 'Сүүлийн өрөөнүүд:' : 'Recent Rooms:'}</span>
            </div>
            <div className="history-sessions-chips">
              {state.roomHistoryLinks.map((link) => {
                const isSelected = link.id === selectedId;
                return (
                  <button
                    key={link.id}
                    type="button"
                    className={`history-session-chip${isSelected ? ' is-selected' : ''}`}
                    onClick={() => {
                      setSelectedId(link.id);
                    }}
                  >
                    <span className="session-chip-room">#{link.roomId}</span>
                    <span className="session-chip-time">{formatRelative(link.startedAt)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {state.connectionStatus !== 'connected' && (
          <p className="history-status-msg" role="status">
            {isMn ? 'Холболт тасарсан' : 'Disconnected'}
          </p>
        )}

        {state.error && (
          <p role="alert" className="history-error">
            {state.error}
          </p>
        )}

        {!history && !state.error && state.connectionStatus === 'connected' && (
          state.roomHistoryLinks.length === 0 ? (
            <div className="history-empty-state">
              <span>🎲</span>
              <p>{isMn ? 'Өрөөний тоглолтын түүх одоогоор алга байна.' : 'No room history found yet.'}</p>
            </div>
          ) : (
            <div className="history-empty-state">
              <p>{isMn ? 'Уншиж байна...' : 'Loading history...'}</p>
            </div>
          )
        )}

        {history && (
          <>
            {/* Champion & Summary Card */}
            <div className="history-summary-card">
              <div className="summary-left">
                {champion && (
                  <div className="champion-badge">
                    <span className="champion-crown">👑</span>
                    <div className="champion-info">
                      <span className="champion-label">{isMn ? 'Шилдэг тоглогч' : 'Session Champion'}</span>
                      <span className="champion-name">{champion.name}</span>
                    </div>
                  </div>
                )}
                <div className="session-meta-pill">
                  {history.games.length} {isMn ? 'тоглолт дууссан' : 'games completed'}
                </div>
              </div>

              {history.games.length > 0 && (
                <button
                  type="button"
                  className="history-copy-button"
                  onClick={copyStandings}
                  title={isMn ? 'Үр дүнг хуулах' : 'Copy standings'}
                >
                  {copied
                    ? isMn ? '✓ Хууллаа!' : '✓ Copied!'
                    : isMn ? '📋 Дүн хуулах' : '📋 Copy Standings'}
                </button>
              )}
            </div>

            {/* Segmented Tabs */}
            <div className="history-tabs-container">
              <div className="history-tabs" role="tablist">
                <button
                  role="tab"
                  className={`history-tab-btn${tab === 'standings' ? ' is-active' : ''}`}
                  aria-selected={tab === 'standings'}
                  onClick={() => setTab('standings')}
                >
                  🏆 {isMn ? 'Эцсийн оноо & Хожил' : 'Final Standings'}
                </button>
                <button
                  role="tab"
                  className={`history-tab-btn${tab === 'games' ? ' is-active' : ''}`}
                  aria-selected={tab === 'games'}
                  onClick={() => setTab('games')}
                >
                  🎮 {isMn ? 'Үеүүдийн түүх' : 'Round History'} ({history.games.length})
                </button>
              </div>
            </div>

            {/* Tab 1: Simplified Standings */}
            {tab === 'standings' && (
              <div className="history-standings-list">
                {totals.length === 0 ? (
                  <p className="history-no-data">{isMn ? 'Тоглолт дуусаагүй байна.' : 'No finished games.'}</p>
                ) : (
                  totals.map((p, idx) => {
                    const wins = p.big2 + p.muushig + p.twentyone + p.daaluu;
                    const winPct = p.played > 0 ? Math.round((wins / p.played) * 100) : 0;
                    const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`;
                    const avatar = getUserAvatar(p.playerId, p.name);
                    const lobbyMember = state.lobby?.players.find(
                      (lp) => lp.name === p.name || lp.playerId === p.playerId,
                    );
                    const playerUserId = p.userId || lobbyMember?.userId;
                    const isMe = (state.profile && playerUserId === state.profile.id) || (state.profile && p.name === state.profile.displayName);

                    return (
                      <div
                        key={p.playerId}
                        className={`history-standing-row${idx === 0 ? ' is-winner' : ''}${isMe ? ' is-me' : ''}`}
                        onClick={() => {
                          if (playerUserId) {
                            setInspectedUserId({ userId: playerUserId, name: p.name });
                          }
                        }}
                        style={{ cursor: playerUserId ? 'pointer' : 'default' }}
                        title={playerUserId ? (isMn ? `${p.name} профайл үзэх` : `View ${p.name}'s profile`) : undefined}
                      >
                        <div className="standing-rank">{medal}</div>

                        <div
                          className="standing-avatar"
                          style={{
                            background: avatar.gradient,
                            borderColor: avatar.borderColor,
                          }}
                        >
                          {avatar.initial}
                        </div>

                        <div className="standing-info">
                          <span className="standing-name">
                            {p.name}
                            {p.isBot && <small className="bot-tag">({isMn ? 'Бот' : 'Bot'})</small>}
                            {isMe && <small className="me-tag">({isMn ? 'Та' : 'You'})</small>}
                          </span>
                          <span className="standing-ratio">
                            {p.played} {isMn ? 'тоглосон' : 'played'} · {winPct}% {isMn ? 'хожил' : 'win rate'}
                          </span>
                        </div>

                        <div className="standing-wins">
                          <span className="wins-count">{wins}</span>
                          <span className="wins-label">{isMn ? 'хожил' : 'wins'}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Tab 2: Simplified Game Rounds */}
            {tab === 'games' && (
              <div className="history-games-list">
                {history.games.length === 0 ? (
                  <p className="history-no-data">{isMn ? 'Тоглолт дуусаагүй байна.' : 'No finished games.'}</p>
                ) : (
                  [...history.games].reverse().map((game, index) => {
                    const winner = game.players.find((pl) => pl.playerId === game.winnerId);
                    const winnerAvatar = winner ? getUserAvatar(winner.playerId, winner.name) : null;
                    const roundNum = history.games.length - index;

                    const winnerMember = state.lobby?.players.find(
                      (lp) => lp.name === winner?.name || lp.playerId === winner?.playerId,
                    );
                    const winnerUserId = winner?.userId || winnerMember?.userId;

                    return (
                      <div key={game.id} className="history-game-card">
                        <div className="game-card-left">
                          <span className="game-round-pill">
                            {isMn ? `Үе ${roundNum}` : `Round ${roundNum}`}
                          </span>
                          <span className="game-type-badge">{formatGameTypeName(game.gameType)}</span>
                        </div>

                        <div
                          className="game-card-winner"
                          onClick={() => {
                            if (winnerUserId) {
                              setInspectedUserId({ userId: winnerUserId, name: winner!.name });
                            }
                          }}
                          style={{ cursor: winnerUserId ? 'pointer' : 'default' }}
                          title={winnerUserId ? (isMn ? `${winner?.name} профайл үзэх` : `View ${winner?.name}'s profile`) : undefined}
                        >
                          {winnerAvatar && (
                            <span
                              className="winner-mini-avatar"
                              style={{ background: winnerAvatar.gradient }}
                            >
                              {winnerAvatar.initial}
                            </span>
                          )}
                          <div className="winner-details">
                            <span className="winner-label">{isMn ? 'Ялагч' : 'Winner'}</span>
                            <span className="winner-name">🏆 {winner?.name ?? '-'}</span>
                          </div>
                        </div>

                        <div className="game-card-time">{formatRelative(game.finishedAt)}</div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Profile inspection modal when clicking players in room history */}
      {inspectedUserId && (
        <PlayerProfileModal
          userId={inspectedUserId.userId}
          initialPlayerName={inspectedUserId.name}
          onClose={() => setInspectedUserId(null)}
        />
      )}
    </section>
  );
}
